import { User as UserIcon, MapPin, Award, TrendingUp, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { getPaginatedUsers, useQuery, getUserById, updateUserByAdmin, updateUserPlatformRole, adminSetUserPassword, softDeleteUser, bulkSoftDeleteUsers } from 'wasp/client/operations';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { type User } from 'wasp/entities';
import { useNavigate } from 'react-router-dom';
import useDebounce from '../../../client/hooks/useDebounce';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import AdminTableLayout from '../../components/AdminTableLayout';
import LoadingSpinner from '../../layout/LoadingSpinner';
import { AdminFlyoutPane, FlyoutSection, FlyoutField, FlyoutActions } from '../../components/AdminFlyoutPane';
import { toast } from '../../../hooks/use-toast';
import { useAuth } from 'wasp/client/auth';
import { getRoleLabel, getRoleBadgeColor } from '../../../client/lib/platformRole';

interface UsersTableProps {
  searchTerm?: string;
  viewMode?: 'cards' | 'list' | 'table';
  roleFilter?: string;
}

const UsersTable = ({ viewMode = 'table', searchTerm: searchTermProp = '', roleFilter }: UsersTableProps) => {
  const navigate = useNavigate();
  const { data: currentAuthUser } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [flyoutMode, setFlyoutMode] = useState<'view' | 'edit'>('view');
  const [showLocationsFlyout, setShowLocationsFlyout] = useState(false);

  const [showSetPassword, setShowSetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSettingPassword, setIsSettingPassword] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Editable fields
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPrimaryLocation, setEditPrimaryLocation] = useState('');
  const [editRole, setEditRole] = useState<'USER' | 'MODERATOR' | 'ADMINISTRATOR' | 'PLATFORM_OWNER'>('USER');
  const [locationSearch, setLocationSearch] = useState('');
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const locationSearchRef = useRef<HTMLInputElement | null>(null);

  const debouncedSearchTerm = useDebounce(searchTermProp, 300);

  const skipPages = currentPage - 1;

  const { data, isLoading, refetch } = useQuery(getPaginatedUsers, {
    skipPages,
    pageSize: itemsPerPage,
    filter: {
      ...(debouncedSearchTerm && { emailContains: debouncedSearchTerm }),
      ...(roleFilter && { platformRole: roleFilter as any }),
    },
  });

  // Fetch selected user details for flyout
  const { data: selectedUser, isLoading: loadingUser } = useQuery(
    getUserById,
    { userId: selectedUserId || '' },
    { enabled: !!selectedUserId, keepPreviousData: true }
  );

  // Focus the location search input when the dropdown opens
  useEffect(() => {
    if (isLocationDropdownOpen) {
      setTimeout(() => {
        locationSearchRef.current?.focus();
      }, 0);
    }
  }, [isLocationDropdownOpen]);

  useEffect(
    function backToPageOne() {
      setCurrentPage(1);
    },
    [debouncedSearchTerm, itemsPerPage, roleFilter]
  );

  // Refetch when itemsPerPage changes
  useEffect(() => {
    refetch();
  }, [itemsPerPage, refetch]);

  // Clear selection when page changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [currentPage, debouncedSearchTerm]);

  // Initialize edit fields when user is selected
  useEffect(() => {
    if (selectedUser) {
      setEditDisplayName(selectedUser.username || '');
      setEditEmail(selectedUser.email || '');
      setEditPrimaryLocation(selectedUser.primaryLocationId?.toString() || '');
      setEditRole(((selectedUser as any).platformRole as any) || 'USER');
    }
  }, [selectedUser]);

  // Check if any changes have been made
  const hasChanges = selectedUser ? (
    editDisplayName !== (selectedUser.username || '') ||
    editEmail !== (selectedUser.email || '') ||
    editPrimaryLocation !== (selectedUser.primaryLocationId?.toString() || '') ||
    editRole !== (((selectedUser as any).platformRole as any) || 'USER')
  ) : false;

  const handleUserClick = (userId: number | string) => {
    setSelectedUserId(userId.toString());
    setFlyoutMode('view');
  };

  const handleEdit = () => {
    setFlyoutMode('edit');
  };

  const handleSave = async () => {
    if (!selectedUser) return;

    try {
      await updateUserByAdmin({
        userId: selectedUser.id,
        username: editDisplayName || undefined,
        email: editEmail || undefined,
        primaryLocationId: editPrimaryLocation || undefined,
      });

      // Update platform role if it changed
      const currentRole = ((selectedUser as any).platformRole as any) || 'USER';
      if (editRole !== currentRole && !isCurrentUser) {
        await updateUserPlatformRole({
          userId: selectedUser.id,
          platformRole: editRole as 'USER' | 'MODERATOR' | 'ADMINISTRATOR',
        });
      }

      await refetch();
      setFlyoutMode('view');

      toast({
        title: "Success",
        description: "User updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update user",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedUserId) return;
    if (!window.confirm('Move this user to the recycle bin? Their content and information will be preserved.')) return;

    try {
      await softDeleteUser({ userId: selectedUserId });
      setSelectedUserId(null);
      setFlyoutMode('view');
      await refetch();
      toast({ title: "User moved to recycle bin" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to delete user", variant: "destructive" });
    }
  };

const handleSetPassword = async () => {
    if (!selectedUserId) return;
    if (newPassword.length < 8) {
      toast({ title: 'Password too short', description: 'Password must be at least 8 characters.', variant: 'destructive' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    setIsSettingPassword(true);
    try {
      await adminSetUserPassword({ userId: selectedUserId, newPassword });
      toast({ title: 'Password updated', description: 'The user can now log in with the new password.' });
      setShowSetPassword(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to set password', variant: 'destructive' });
    } finally {
      setIsSettingPassword(false);
    }
  };

  // Selection helpers
  const allPageIds = data?.users?.map((u: any) => u.id) ?? [];
  const allSelected = allPageIds.length > 0 && allPageIds.every((id: any) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allPageIds));
    }
  };

  const toggleSelectOne = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Move ${ids.length} user(s) to the recycle bin? Their content and information will be preserved.`)) return;

    setIsBulkDeleting(true);
    try {
      const result = await bulkSoftDeleteUsers({ userIds: ids });
      setSelectedIds(new Set());
      await refetch();
      toast({ title: `${(result as any).count} user(s) moved to recycle bin` });
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Bulk delete failed", variant: "destructive" });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Navigation helpers
  const currentUserIndex = data?.users?.findIndex((u: any) => u.id.toString() === selectedUserId) ?? -1;
  const hasPrevious = currentUserIndex > 0;
  const hasNext = currentUserIndex >= 0 && currentUserIndex < (data?.users?.length ?? 0) - 1;

  const handleNavigatePrevious = () => {
    if (hasPrevious && data?.users) {
      setSelectedUserId(data.users[currentUserIndex - 1].id.toString());
    }
  };

  const handleNavigateNext = () => {
    if (hasNext && data?.users) {
      setSelectedUserId(data.users[currentUserIndex + 1].id.toString());
    }
  };

  const isCurrentUser = currentAuthUser?.id === selectedUser?.id;

  // Final Render Logic
  return (
    <>
      {/* Shared Bulk Action Bar */}
      {someSelected && (
        <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-2xl rounded-2xl px-6 py-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="flex flex-col">
            <span className="text-sm font-bold text-gray-900 dark:text-white">
              {selectedIds.size} Users Selected
            </span>
            <span className="text-xs text-gray-500">Perform bulk actions</span>
          </div>
          <div className="w-px h-8 bg-gray-200 dark:bg-gray-700 mx-2" />
          <Button
            variant="destructive"
            size="sm"
            onClick={handleBulkDelete}
            disabled={isBulkDeleting}
            className="rounded-xl px-4 font-semibold"
          >
            {isBulkDeleting ? 'Deleting…' : 'Move to Recycle Bin'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedIds(new Set())}
            className="rounded-xl text-gray-500"
          >
            Clear
          </Button>
        </div>
      )}

      {/* Main Content (Table or Cards) */}
      {viewMode === 'table' ? (
        <AdminTableLayout
          isLoading={isLoading}
          columns={[
            { label: 'Select', span: 1 },
            { label: 'User Details', span: 5 },
            { label: 'Role', span: 2 },
            { label: 'Primary Location', span: 3 },
            { label: 'Earnings', span: 1 },
          ]}
          headerFilters={
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-1 flex items-center justify-center">
                <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} className="translate-y-[2px]" />
              </div>
              <div className="col-span-11" />
            </div>
          }
          pagination={{
            currentPage,
            totalPages: data?.totalPages || 1,
            onPageChange: setCurrentPage,
            itemsPerPage,
            onItemsPerPageChange: setItemsPerPage,
          }}
        >
          {data?.users?.map((user: any) => (
            <div
              key={user.id}
              className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer items-center border-b border-gray-100 dark:border-gray-800 last:border-0"
              onClick={() => handleUserClick(user.id)}
            >
              <div className="col-span-1 flex items-center justify-center" onClick={(e) => { e.stopPropagation(); toggleSelectOne(user.id, e as any); }}>
                <Checkbox checked={selectedIds.has(user.id)} onCheckedChange={() => {}} />
              </div>
              <div className="col-span-5 flex flex-col min-w-0">
                <span className="font-bold text-gray-900 dark:text-white truncate">{user.username || 'Anonymous User'}</span>
                <span className="text-xs text-gray-500 truncate">{user.email}</span>
              </div>
              <div className="col-span-2">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-lg font-bold ${getRoleBadgeColor(user.platformRole)}`}>
                  {getRoleLabel(user.platformRole)}
                </span>
              </div>
              <div className="col-span-3 flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                <MapPin size={14} className="shrink-0" />
                <span className="text-sm truncate">{user.primaryLocation?.name || 'N/A'}</span>
              </div>
              <div className="col-span-1 flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                <Award size={14} className="shrink-0 text-amber-500" />
                <span className="text-sm font-medium">{user.pointsEarned || 0}</span>
              </div>
            </div>
          ))}
        </AdminTableLayout>
      ) : (
        <div className="p-6 pb-24">
          {isLoading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {data?.users?.map((user: any) => (
                <div
                  key={user.id}
                  className="group relative bg-white dark:bg-sidebar-active border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden hover:shadow-xl hover:border-blue-400 dark:hover:border-blue-500 transition-all duration-300 cursor-pointer"
                  onClick={() => handleUserClick(user.id)}
                >
                  <div className="px-5 pt-5 flex justify-between items-start">
                    <div className="w-12 h-12 bg-gray-100 dark:bg-sidebar-hover rounded-xl flex items-center justify-center text-gray-500 group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20 group-hover:text-blue-500 transition-colors">
                      <UserIcon size={24} />
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-lg font-bold ${getRoleBadgeColor((user as any).platformRole)}`}>
                      {getRoleLabel((user as any).platformRole)}
                    </span>
                  </div>
                  <div className="px-5 py-6">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate mb-1">{user.username || 'Anonymous User'}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate mb-4">{user.email}</p>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                        <MapPin size={14} className="text-gray-400" />
                        <span className="truncate">{user.primaryLocation?.name || 'No location set'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                        <Award size={14} className="text-gray-400" />
                        <span>{user.pointsEarned || 0} Points</span>
                      </div>
                    </div>
                  </div>
                  <div className="px-5 py-4 bg-gray-50 dark:bg-sidebar-hover/50 border-t border-gray-100 dark:border-gray-800 flex justify-between items-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">View Details</span>
                    <Checkbox checked={selectedIds.has(user.id)} onCheckedChange={() => {}} onClick={(e) => toggleSelectOne(user.id, e as any)} className="rounded-md" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Card Pagination */}
          {!!data?.users && data.users.length > 0 && (
            <div className="mt-8 flex items-center justify-between border-t border-gray-100 dark:border-gray-800 pt-6">
              <div className="text-sm text-gray-500">Page {currentPage} of {data.totalPages}</div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={currentPage === data.totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Shared Flyout and Dialogs */}
      <AdminFlyoutPane
        open={!!selectedUserId}
        onOpenChange={(open) => { if (!open) { setSelectedUserId(null); setFlyoutMode('view'); } }}
        title={flyoutMode === 'view' ? 'View User' : 'Edit User'}
        width="2xl"
        mode={flyoutMode}
        onNavigatePrevious={handleNavigatePrevious}
        onNavigateNext={handleNavigateNext}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        headerActions={flyoutMode === 'view' ? (
          <>
            <Button variant="destructive" size="sm" onClick={handleDelete} disabled={isCurrentUser} className="bg-red-600 hover:bg-red-700 text-white w-[110px]">Delete</Button>
            <Button variant="outline" size="sm" onClick={() => { setNewPassword(''); setConfirmPassword(''); setShowSetPassword(true); }} className="w-[140px]">Set Password</Button>
            <Button onClick={handleEdit} size="sm" className="w-[110px]">Edit</Button>
          </>
        ) : (
          <>
            <Button variant="outline" size="sm" onClick={() => setFlyoutMode('view')} className="w-[110px]">{hasChanges ? 'Cancel' : 'Close'}</Button>
            <Button onClick={handleSave} size="sm" className="w-[110px]" disabled={!hasChanges}>Save</Button>
          </>
        )}
      >
        {loadingUser && !selectedUser ? (
          <div className="flex justify-center py-8"><LoadingSpinner /></div>
        ) : selectedUser ? (
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
            <div className="grid" style={{ gridTemplateColumns: 'minmax(120px, 30%) 1fr' }}>
              <div className="col-span-2 py-3 px-4 bg-gray-50 dark:bg-sidebar-hover border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-widest">User Information</h3>
              </div>
              <div className="border-b border-r border-gray-100 dark:border-gray-800 py-4 px-5 bg-gray-50/50 dark:bg-sidebar-active/50"><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Display Name</span></div>
              <div className="border-b border-gray-100 dark:border-gray-800 py-4 px-5">{flyoutMode === 'edit' ? <Input value={editDisplayName} onChange={(e) => setEditDisplayName(e.target.value)} className="h-9" /> : <span className="text-sm text-gray-900 dark:text-white font-medium">{selectedUser.username || 'Not set'}</span>}</div>
              <div className="border-b border-r border-gray-100 dark:border-gray-800 py-4 px-5 bg-gray-50/50 dark:bg-sidebar-active/50"><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Email</span></div>
              <div className="border-b border-gray-100 dark:border-gray-800 py-4 px-5">{flyoutMode === 'edit' ? <Input value={editEmail} onChange={(e) => setEditEmail(e.target.value)} className="h-9" /> : <span className="text-sm text-gray-900 dark:text-white font-medium">{selectedUser.email}</span>}</div>
              <div className="col-span-2 py-3 px-4 bg-gray-50 dark:bg-sidebar-hover border-b border-gray-200 dark:border-gray-700 mt-4"><h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-widest">Locations</h3></div>
              <div className="border-b border-r border-gray-100 dark:border-gray-800 py-4 px-5 bg-gray-50/50 dark:bg-sidebar-active/50"><span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Primary</span></div>
              <div className="border-b border-gray-100 dark:border-gray-800 py-4 px-5"><div className="flex items-center gap-2"><MapPin size={16} className="text-gray-400" /><span className="text-sm">{selectedUser.primaryLocation?.name || 'None'}</span></div></div>
            </div>
          </div>
        ) : null}
      </AdminFlyoutPane>

      <Dialog open={showSetPassword} onOpenChange={setShowSetPassword}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Set User Password</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>New Password</Label><Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} /></div>
            <div className="space-y-2"><Label>Confirm Password</Label><Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowSetPassword(false)}>Cancel</Button><Button onClick={handleSetPassword} disabled={isSettingPassword}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UsersTable;



